// What the overview draws, worked out from the fields the form collects.
//
// Here rather than in the component so the card and the form can never
// disagree about what the same values add up to — and so each is a function a
// test can put a profile through, which is cheaper than rendering a card to
// find out how eight years and no months reads.

import type { DietitianProfile } from "./types/settings.types";

/** The name as the profile is headed, salutation included where there is one. */
export const fullName = (profile: DietitianProfile): string =>
  [
    profile.salutation ? `${profile.salutation}.` : "",
    profile.firstName,
    profile.lastName,
  ]
    .filter(Boolean)
    .join(" ");

/**
 * Time in practice, in words: "8 years", "4 months", "8 years 4 months".
 *
 * A figure of zero is left out rather than written — "8 years 0 months" is a
 * form's answer, not a person's — and two zeroes together read as no answer at
 * all, which is what the empty string is for.
 */
export const experienceLabel = (profile: DietitianProfile): string => {
  const years = Number(profile.experienceYears) || 0;
  const months = Number(profile.experienceMonths) || 0;

  return [
    years > 0 ? `${years} ${years === 1 ? "year" : "years"}` : "",
    months > 0 ? `${months} ${months === 1 ? "month" : "months"}` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * The address on one line — the street, then the city, state and pincode the
 * three fields under it hold. Whatever is missing is simply not written, so a
 * half-filled address reads as a short one rather than as a line of commas.
 */
export const addressLabel = (profile: DietitianProfile): string => {
  const region = [profile.state, profile.pincode].filter(Boolean).join(" ");

  return [profile.address, profile.city, region].filter(Boolean).join(", ");
};
